/**
 * Browser-side language detection.
 *
 * Resolution order (first hit wins):
 *   1. Explicit user setting persisted to localStorage
 *   2. URL `?lang=xx` query parameter
 *   3. `navigator.language` / `navigator.languages`
 *   4. `DEFAULT_LOCALE`
 *
 * Server-side resolution (Accept-Language) lives next to the API routes; this
 * module is for the client only.
 */

import { coerceLocale, isSupportedLocale, DEFAULT_LOCALE, type SupportedLocale } from "./config";

const STORAGE_KEY = "fixcontrol.locale";

/**
 * The same preference, mirrored into a cookie.
 *
 * localStorage is invisible to the server, so for as long as the choice lived
 * only there, EVERY server render was in the default locale and the client
 * re-rendered the entire application into the user's language on mount. For a
 * Dutch operator that meant every label, button, nav entry, table header and
 * status pill in the app changed width one tick after first paint — a reflow
 * no amount of skeleton work can help with, because the geometry that moves is
 * the text itself.
 *
 * The cookie is a plain, non-sensitive preference (`nl` or `en`): readable by
 * the server so the first render is already correct, SameSite=Lax so it is not
 * sent on cross-site requests, and deliberately NOT httpOnly — the client
 * writes it.
 */
export const LOCALE_COOKIE = "fc_locale";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

export function readStoredLocale(): SupportedLocale | null {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return isSupportedLocale(v) ? v : null;
  } catch {
    return null;
  }
}

export function writeStoredLocale(locale: SupportedLocale): void {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(STORAGE_KEY, locale); } catch { /* ignore */ }
  // Mirror to the cookie so the NEXT server render is already in this
  // language. A failure here is not fatal — it costs a re-render on the next
  // load, which is where we started.
  try {
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie =
      `${LOCALE_COOKIE}=${locale}; Path=/; Max-Age=${COOKIE_MAX_AGE}; SameSite=Lax${secure}`;
  } catch { /* ignore */ }
}

export function readQueryLocale(): SupportedLocale | null {
  if (typeof window === "undefined") return null;
  try {
    const v = new URL(window.location.href).searchParams.get("lang");
    return isSupportedLocale(v) ? v : null;
  } catch {
    return null;
  }
}

export function readBrowserLocale(): SupportedLocale | null {
  if (typeof navigator === "undefined") return null;
  const candidates: string[] = [];
  if (navigator.languages?.length) candidates.push(...navigator.languages);
  if (navigator.language) candidates.push(navigator.language);
  for (const c of candidates) {
    const coerced = coerceLocale(c);
    // coerceLocale falls back to DEFAULT_LOCALE for unknown tags; we only want
    // a hit when the tag actually matched something we support.
    if (isSupportedLocale(c) || isSupportedLocale(c.split(/[-_]/)[0])) {
      return coerced;
    }
  }
  return null;
}

/**
 * Pick the locale to render with on the client.
 *
 * `tenantDefault` lets a tenant-branded surface (e.g. the public intake page)
 * override the browser default with the company's preferred language. URL
 * params and stored user prefs still win.
 */
export function resolveClientLocale(opts: {
  tenantDefault?: SupportedLocale | null;
} = {}): SupportedLocale {
  return (
    readQueryLocale()
    ?? readStoredLocale()
    ?? opts.tenantDefault
    ?? readBrowserLocale()
    ?? DEFAULT_LOCALE
  );
}

export const LOCALE_STORAGE_KEY = STORAGE_KEY;
